"use client";

import { useState } from "react";
import { Check, Mail, Send } from "lucide-react";
import TextareaAutosize from "react-textarea-autosize";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import AgentActionButton, {
  AGENT_ACTION_DONE_CLASS,
  AGENT_ACTION_VARIANTS,
} from "./AgentActionButton";
import { buildCheckInDraft } from "@/lib/reports/coaching-checkin-draft";

/**
 * One-click coaching check-in for a flagged teammate. The trigger opens a
 * preview dialog prefilled with the shared default draft (same builder the
 * server falls back to), which the manager can personalize before sending.
 *
 * The actual send, including its pending/done/error states, is delegated to
 * AgentActionButton so there's a single implementation of "run an agent action";
 * this component only owns the dialog + editable draft. Reusable anywhere a
 * manager sees a person who needs attention — the home dashboard "Needs
 * attention" rows, the person detail page, and the reports assistant's
 * assignment-health card all render it. The recipient is resolved server-side
 * from `userId`; `tag` ("down" declining or "warn" low score) only steers the
 * default wording.
 */
export default function CheckInButton({
  userId,
  name,
  tag = "warn",
  label = "Check in",
  size = "sm",
  className,
  onSent,
}) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [message, setMessage] = useState("");

  if (!userId) return null;

  const firstName = name ? name.split(" ")[0] || name : "this teammate";

  const openDialog = () => {
    setMessage(buildCheckInDraft({ name, tag }));
    setOpen(true);
  };

  return (
    <>
      <Button
        type="button"
        size={size}
        variant="outline"
        disabled={sent}
        onClick={openDialog}
        className={cn(
          "gap-1.5 font-medium transition-colors",
          sent ? AGENT_ACTION_DONE_CLASS : AGENT_ACTION_VARIANTS.secondary,
          className,
        )}
      >
        {sent ? (
          <Check className="w-3.5 h-3.5" />
        ) : (
          <Send className="w-3.5 h-3.5" />
        )}
        {sent ? "Check-in sent" : label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Send a check-in to {firstName}</DialogTitle>
            <DialogDescription>
              Personalize the message before it&apos;s emailed. {firstName} will
              see it as a note from you.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <label
              htmlFor="checkin-message"
              className="text-xs font-medium text-gray-500"
            >
              Message
            </label>
            <TextareaAutosize
              id="checkin-message"
              minRows={6}
              maxRows={16}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              className="w-full resize-y rounded-md border border-slate-200 bg-white p-3 text-sm leading-6 text-gray-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400/20"
              placeholder="Write a short, encouraging note…"
            />
          </div>

          <p className="flex items-start gap-1.5 text-xs text-gray-500">
            <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Sent via email from your Salesably account. {firstName} sees your
            name as the sender.
          </p>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <AgentActionButton
              size="default"
              disabled={!message.trim()}
              onComplete={(result) => {
                if (result?.success) {
                  setSent(true);
                  setOpen(false);
                  onSent?.(result);
                }
              }}
              action={{
                kind: "send-coaching-checkin",
                label: "Send check-in",
                successLabel: "Sent",
                pendingLabel: "Sending…",
                style: "primary",
                payload: { userIds: [userId], tag, message: message.trim() },
              }}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
